import { bridge, getMemberJWTTokenKey, getWebBaseURL, isApp } from '../utils';
import { AxiosError, HttpStatusCode } from 'axios';
import React from 'react';
import cookie from 'js-cookie';
import type { NavigationProp, NavigationState } from '@react-navigation/native';
import RNRestart from 'react-native-restart';
import { moveToToday } from '../utils/navigator/useCommonNavigator/moveToToday';
import { DefaultErrorBoundaryView } from './DefaultErrorBoundaryView';
import { ApiError } from '../api/httpClient';

interface ErrorBoundaryProps extends React.ComponentProps<'div'> {
  showRefreshButton?: boolean;
  title?: string;
  ErrorFallback?: (e: Error) => React.ReactNode;
  instanceId?: string;
  navigation?: Omit<
    NavigationProp<ReactNavigation.RootParamList>,
    'getState'
  > & {
    getState(): NavigationState | undefined;
  };
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  {
    statusCode?: number;
    descriptions: string[];
    error?: Error;
  }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      statusCode: undefined,
      descriptions: [],
    };
  }

  static getDerivedStateFromError(error: Error) {
    if (error instanceof AxiosError) {
      return {
        serverError: true,
        statusCode: error.status,
        descriptions: [error.message],
        error,
      };
    }

    if (error instanceof ApiError) {
      return {
        serverError: true,
        statusCode: error.status,
        descriptions: [error.message],
        error,
      };
    }

    return { error };
  }

  onGoHome() {
    moveToToday();
  }

  async onRefresh(statusCode?: number) {
    if (statusCode === HttpStatusCode.Unauthorized && !isApp()) {
      cookie.remove(await getMemberJWTTokenKey());
      const baseURL = await getWebBaseURL();
      window.location.replace(
        `${baseURL}/login?redirect=${window.location.href}`,
      );
      return;
    }

    if (isApp()) {
      bridge.closeInstance({ instanceId: this.props.instanceId });
      return;
    }

    window.location.reload();
  }

  handleClick(statusCode?: number) {
    try {
      if (!__DEV__) RNRestart.restart();
    } catch {
      /* empty */
    }

    if (statusCode === HttpStatusCode.NotFound) {
      this.onGoHome();
      return;
    }

    this.onRefresh(statusCode);
  }

  render() {
    const {
      showRefreshButton = true,
      title = '오류가 발생했어요',
      children,
      ErrorFallback,
    } = this.props;
    const { error, statusCode, descriptions } = this.state;
    if (error === undefined) return children;

    let pageTitle = error.message ?? title;
    let buttonText = '새로고침';

    if (statusCode === HttpStatusCode.NotFound) {
      pageTitle = '페이지를 찾을 수 없어요';
      buttonText = '홈으로 이동';
    }

    if (ErrorFallback && error) {
      return ErrorFallback(error);
    }

    return (
      <DefaultErrorBoundaryView
        buttonText={buttonText}
        pageTitle={pageTitle}
        descriptions={descriptions}
        showRefreshButton={showRefreshButton}
        onPressButton={() => this.handleClick(statusCode)}
      />
    );
  }
}
